/**
 * Dates and money as every page renders them. A formatter lives here rather
 * than beside the page that first needed it, so two pages cannot come to
 * disagree about what a day or an amount looks like.
 */

const DAY_AND_MONTH = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "3 Sep", or the year too when it was not this one.
 *
 * A bare `YYYY-MM-DD` is a day rather than an instant — what a reported
 * contact carries — and JavaScript reads one as UTC midnight, which renders as
 * the day before anywhere west of Greenwich. It is built as a local day here
 * instead, because the day somebody wrote down is not a moment to convert.
 */
export function formatDay(at: string | null): string | null {
  if (at === null) return null;
  const parts = DAY_ONLY.exec(at);
  const date =
    parts === null
      ? new Date(at)
      : new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() === new Date().getFullYear()) return DAY_AND_MONTH.format(date);
  return `${DAY_AND_MONTH.format(date)} ${date.getFullYear()}`;
}

/** Today as a `YYYY-MM-DD` day, which is what a date input and a report take. */
export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** "2 hours ago". A run is judged by how stale it is, not by its timestamp. */
export function formatWhen(at: string | null): string {
  if (at === null) return 'just now';
  const moment = new Date(at);
  if (Number.isNaN(moment.getTime())) return '';
  const minutes = Math.round((moment.getTime() - Date.now()) / 60_000);
  if (Math.abs(minutes) < 60) return RELATIVE.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return RELATIVE.format(hours, 'hour');
  return RELATIVE.format(Math.round(hours / 24), 'day');
}

const EUR = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** "€11.8M". A round's size is read at a glance and never added up. */
export function formatEur(amount: number): string {
  return EUR.format(amount);
}

const COUNT = new Intl.NumberFormat('en-GB');

/** "4,006". A backlog's size is read, so the thousands are separated. */
export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** "1 year", "6 years". */
export function formatYears(value: number): string {
  return `${value} ${value === 1 ? 'year' : 'years'}`;
}
